import React, { useState, useEffect, useCallback } from 'react';
import { Card, Checkbox, LegacyStack, Tooltip, Spinner, Page, TextContainer, Icon, TextField, InlineStack, Text, Box, Select } from '@shopify/polaris';
import { QuestionCircleIcon, ClipboardCheckIcon, ClockIcon, TargetIcon, CalendarIcon, CalendarCheckIcon, MobileIcon } from '@shopify/polaris-icons';
import { IconDisc } from '../ui/CardSurface';
import { SaveBar } from '@shopify/app-bridge-react';
import useAxiosClient from '../axiosClient';
import { useTranslation } from 'react-i18next';

export default function Settings() {
    const { t } = useTranslation();
    const axiosClient = useAxiosClient();
    const [isLoading, setIsLoading] = useState(true);
    const [isDirty, setIsDirty] = useState(false);
    const [showPayrollStatsInPos, setShowPayrollStatsInPos] = useState(true);
    // State variables
    const [shop_name, setShop] = useState('');
    const [breakTimeDeductEnabled, setBreakTimeDeductEnabled] = useState(true);
    const [addSalaryEnabled, setAddSalaryEnabled] = useState(true);

    // Absence calculation settings
    const [excludePaidLeavesFromAbsents, setExcludePaidLeavesFromAbsents] = useState(true);
    const [includeUnpaidLeavesInAbsents, setIncludeUnpaidLeavesInAbsents] = useState(true);
    const [defaultDailyHours, setDefaultDailyHours] = useState('8');

    // NEW: Auto add paid leave setting
    const [autoAddPaidLeaveToSalary, setAutoAddPaidLeaveToSalary] = useState(true);

    // NEW: Auto deduct unpaid leave setting
    const [autoDeductUnpaidLeaveFromSalary, setAutoDeductUnpaidLeaveFromSalary] = useState(false);

    // NEW: Auto deduct absent days setting
    const [autoDeductAbsentDaysFromSalary, setAutoDeductAbsentDaysFromSalary] = useState(false);

    // NEW: Don't allow break after end time setting
    const [dontAllowBreakAfterEndTime, setDontAllowBreakAfterEndTime] = useState(false);
    // NEW: Allow early clock-in setting
    const [allowEarlyClockIn, setAllowEarlyClockIn] = useState(true);
    // NEW: Early clock-in minutes setting
    const [earlyClockInMinutes, setEarlyClockInMinutes] = useState('30');
    // NEW: Time format setting
    const [timeFormat, setTimeFormat] = useState('24h');
    // NEW: Hours format setting
    const [hoursFormat, setHoursFormat] = useState('standard');
    const [shopDomain, setShopDomain] = useState('');
    // NEW: Upload photo before clock in/out setting
    const [uploadPhotoBeforeClockInOut, setUploadPhotoBeforeClockInOut] = useState(false);
    // NEW: Photo is mandatory before clock in/out setting
    const [photoMandatoryBeforeClockInOut, setPhotoMandatoryBeforeClockInOut] = useState(true);
    // NEW: GPS location verification setting
    const [gpsLocationEnabled, setGpsLocationEnabled] = useState(false);
    // NEW: Allow manager to create own and other manager shifts setting
    const [allowManagerCreateOwnAndOtherManagerShifts, setAllowManagerCreateOwnAndOtherManagerShifts] = useState(false);
    // NEW: Holiday rule weekday selection
    const [holidayWeekdays, setHolidayWeekdays] = useState({
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: true,
        sunday: true
    });

    // NEW: Portal feature controls
    const [portalFeatures, setPortalFeatures] = useState({
        enableClockInTile: true,
        enableManagerView: true,
        enableTimeOff: true,
        enableMyProfile: true,
        enableTasklist: true,
        enableViewShifts: true,
        enableCreateSchedule: false,
        enableTimesheet: true
    });

    const [enablePayPeriodTargets, setEnablePayPeriodTargets] = useState(false);
    const [payPeriodType, setPayPeriodType] = useState('');
    const [payPeriodHoursTarget, setPayPeriodHoursTarget] = useState('');

    const [initialSettings, setInitialSettings] = useState({}); // To store initial settings for comparison

    useEffect(() => {
        const fetchSettings = async () => {
            setIsLoading(true);
            try {
                const response = await axiosClient.get('/settings_data');
                const settings = response.data.settings;

                // Always set the shop domain (used for the portal link), even when
                // the shop has no app_settings row yet.
                setShopDomain(response.data.shop || '');

                if (settings) {
                    // Convert string values ("1" or "0") to boolean
                    setBreakTimeDeductEnabled(settings.break_time_deduct_enabled === "1");
                    setAddSalaryEnabled(settings.add_salary_enabled === "1");

                    setShowPayrollStatsInPos(settings.show_payroll_stats_in_pos === 1 || settings.show_payroll_stats_in_pos === undefined);
                    // Set absence calculation settings with defaults if they don't exist yet
                    setExcludePaidLeavesFromAbsents(settings.exclude_paid_leaves_from_absents === 1 || settings.exclude_paid_leaves_from_absents === undefined);
                    setIncludeUnpaidLeavesInAbsents(settings.include_unpaid_leaves_in_absents === 1 || settings.include_unpaid_leaves_in_absents === undefined);
                    setDefaultDailyHours(settings.default_daily_hours || '8');

                    // NEW: Set auto add paid leave setting with default if it doesn't exist
                    setAutoAddPaidLeaveToSalary(settings.auto_add_paid_leave_to_salary === 1 || settings.auto_add_paid_leave_to_salary === undefined);

                    // NEW: Set auto deduct unpaid leave setting with default if it doesn't exist
                    setAutoDeductUnpaidLeaveFromSalary(settings.auto_deduct_unpaid_leave_from_salary === 1);

                    // NEW: Set auto deduct absent days setting with default if it doesn't exist
                    setAutoDeductAbsentDaysFromSalary(settings.auto_deduct_absent_days_from_salary === 1);

                    // NEW: Set don't allow break after end time setting
                    setDontAllowBreakAfterEndTime(settings.dont_allow_break_after_end_time === 1);

                    // NEW: Set allow early clock-in setting with default if it doesn't exist
                    setAllowEarlyClockIn(settings.allow_early_clock_in === 1 || settings.allow_early_clock_in === undefined);

                    // NEW: Set early clock-in minutes setting with default if it doesn't exist
                    setEarlyClockInMinutes(settings.early_clock_in_minutes || '30');

                    // NEW: Set time format setting with default if it doesn't exist
                    setTimeFormat(settings.time_format || '24h');

                    // NEW: Set hours format setting with default if it doesn't exist
                    setHoursFormat(settings.hours_format || 'standard');

                    // NEW: Set upload photo before clock in/out setting
                    setUploadPhotoBeforeClockInOut(settings.upload_photo_before_clock_in_out === 1);

                    // NEW: Set photo mandatory before clock in/out setting with default if it doesn't exist
                    setPhotoMandatoryBeforeClockInOut(settings.photo_mandatory_before_clock_in_out === 1 || settings.photo_mandatory_before_clock_in_out === undefined);

                    // NEW: Set GPS location enabled setting
                    setGpsLocationEnabled(settings.gps_location_enabled === 1);

                    // NEW: Set allow manager to create own and other manager shifts setting
                    setAllowManagerCreateOwnAndOtherManagerShifts(settings.allow_manager_create_own_and_other_manager_shifts === 1);

                    // NEW: Set holiday weekdays with defaults if they don't exist
                    if (settings.holiday_weekdays) {
                        setHolidayWeekdays(JSON.parse(settings.holiday_weekdays));
                    }

                    // NEW: Set portal features with defaults if they don't exist
                    if (settings.portal_features) {
                        const parsedFeatures = JSON.parse(settings.portal_features);
                        setPortalFeatures({
                            enableClockInTile: parsedFeatures.enableClockInTile === 1,
                            enableManagerView: parsedFeatures.enableManagerView === 1,
                            enableTimeOff: parsedFeatures.enableTimeOff === 1,
                            enableMyProfile: parsedFeatures.enableMyProfile === 1,
                            enableTasklist: parsedFeatures.enableTasklist === 1,
                            enableViewShifts: parsedFeatures.enableViewShifts === 1 || parsedFeatures.enableViewShifts === undefined,
                            enableCreateSchedule: parsedFeatures.enableCreateSchedule === 1,
                            enableTimesheet: parsedFeatures.enableTimesheet === 1 || parsedFeatures.enableTimesheet === undefined
                        });
                    }

                    setEnablePayPeriodTargets(settings.enable_pay_period_targets === 1);
                    setPayPeriodType(settings.pay_period_type || '');
                    setPayPeriodHoursTarget(
                        settings.pay_period_hours_target !== null && settings.pay_period_hours_target !== undefined
                            ? String(settings.pay_period_hours_target)
                            : ''
                    );

                    // Save initial settings for comparison
                    setInitialSettings({
                        break_time_deduct_enabled: settings.break_time_deduct_enabled === "1",
                        add_salary_enabled: settings.add_salary_enabled === "1",
                        exclude_paid_leaves_from_absents: settings.exclude_paid_leaves_from_absents === 1 || settings.exclude_paid_leaves_from_absents === undefined,
                        include_unpaid_leaves_in_absents: settings.include_unpaid_leaves_in_absents === 1 || settings.include_unpaid_leaves_in_absents === undefined,
                        default_daily_hours: settings.default_daily_hours || '8',
                        auto_add_paid_leave_to_salary: settings.auto_add_paid_leave_to_salary === 1 || settings.auto_add_paid_leave_to_salary === undefined,
                        auto_deduct_unpaid_leave_from_salary: settings.auto_deduct_unpaid_leave_from_salary === 1,
                        auto_deduct_absent_days_from_salary: settings.auto_deduct_absent_days_from_salary === 1,
                        dont_allow_break_after_end_time: settings.dont_allow_break_after_end_time === 1,
                        allow_early_clock_in: settings.allow_early_clock_in === 1 || settings.allow_early_clock_in === undefined,
                        early_clock_in_minutes: settings.early_clock_in_minutes || '30',
                        time_format: settings.time_format || '24h',
                        hours_format: settings.hours_format || 'standard',
                        upload_photo_before_clock_in_out: settings.upload_photo_before_clock_in_out === 1,
                        photo_mandatory_before_clock_in_out: settings.photo_mandatory_before_clock_in_out === 1 || settings.photo_mandatory_before_clock_in_out === undefined,
                        gps_location_enabled: settings.gps_location_enabled === 1,
                        allow_manager_create_own_and_other_manager_shifts: settings.allow_manager_create_own_and_other_manager_shifts === 1,
                        holiday_weekdays: settings.holiday_weekdays ? JSON.parse(settings.holiday_weekdays) : {
                            monday: false,
                            tuesday: false,
                            wednesday: false,
                            thursday: false,
                            friday: false,
                            saturday: true,
                            sunday: true
                        },
                        show_payroll_stats_in_pos: settings.show_payroll_stats_in_pos === 1 || settings.show_payroll_stats_in_pos === undefined,
                        portal_features: settings.portal_features ? JSON.parse(settings.portal_features) : {
                            enableClockInTile: true,
                            enableManagerView: true,
                            enableTimeOff: true,
                            enableMyProfile: true,
                            enableTasklist: true,
                            enableViewShifts: true,
                            enableCreateSchedule: false,
                            enableTimesheet: true
                        },
                        enable_pay_period_targets: settings.enable_pay_period_targets === 1,
                        pay_period_type: settings.pay_period_type || '',
                        pay_period_hours_target: settings.pay_period_hours_target !== null && settings.pay_period_hours_target !== undefined
                            ? String(settings.pay_period_hours_target)
                            : '',
                    });

                }
            } catch (error) {
                // Error handled silently
            } finally {
                setIsLoading(false);
            }
        };

        fetchSettings();
    }, []);

    const handleCheckboxChange = (setState, newValue) => {
        setState(newValue); // Update the local state
        setIsDirty(true); // Mark the form as dirty
    };

    // NEW: Handle holiday weekday checkbox change
    const handleHolidayWeekdayChange = (day, checked) => {
        setHolidayWeekdays(prev => ({
            ...prev,
            [day]: checked
        }));
        setIsDirty(true);
    };

    // NEW: Handle portal feature checkbox change
    const handlePortalFeatureChange = (feature, checked) => {
        setPortalFeatures(prev => ({
            ...prev,
            [feature]: checked
        }));
        setIsDirty(true);
    };

    const handleTextFieldChange = (value) => {
        setDefaultDailyHours(value);
        setIsDirty(true);
    };

    const handleEarlyClockInMinutesChange = (value) => {
        setEarlyClockInMinutes(value);
        setIsDirty(true);
    };

    const handleSave = async () => {
        try {
            const payload = {
                break_time_deduct_enabled: breakTimeDeductEnabled ? 1 : 0,
                add_salary_enabled: addSalaryEnabled ? 1 : 0,
                exclude_paid_leaves_from_absents: excludePaidLeavesFromAbsents ? 1 : 0,
                include_unpaid_leaves_in_absents: includeUnpaidLeavesInAbsents ? 1 : 0,
                default_daily_hours: parseInt(defaultDailyHours) || 8,
                auto_add_paid_leave_to_salary: autoAddPaidLeaveToSalary ? 1 : 0,
                auto_deduct_unpaid_leave_from_salary: autoDeductUnpaidLeaveFromSalary ? 1 : 0,
                auto_deduct_absent_days_from_salary: autoDeductAbsentDaysFromSalary ? 1 : 0,
                dont_allow_break_after_end_time: dontAllowBreakAfterEndTime ? 1 : 0,
                allow_early_clock_in: allowEarlyClockIn ? 1 : 0,
                early_clock_in_minutes: parseInt(earlyClockInMinutes) || 30,
                time_format: timeFormat,
                hours_format: hoursFormat,
                upload_photo_before_clock_in_out: uploadPhotoBeforeClockInOut ? 1 : 0,
                photo_mandatory_before_clock_in_out: photoMandatoryBeforeClockInOut ? 1 : 0,
                gps_location_enabled: gpsLocationEnabled ? 1 : 0,
                allow_manager_create_own_and_other_manager_shifts: allowManagerCreateOwnAndOtherManagerShifts ? 1 : 0,
                holiday_weekdays: holidayWeekdays, // NEW: Include the holiday weekdays setting
                show_payroll_stats_in_pos: showPayrollStatsInPos ? 1 : 0,
                portal_features: {
                    enableClockInTile: portalFeatures.enableClockInTile ? 1 : 0,
                    enableManagerView: portalFeatures.enableManagerView ? 1 : 0,
                    enableTimeOff: portalFeatures.enableTimeOff ? 1 : 0,
                    enableMyProfile: portalFeatures.enableMyProfile ? 1 : 0,
                    enableTasklist: portalFeatures.enableTasklist ? 1 : 0,
                    enableViewShifts: portalFeatures.enableViewShifts ? 1 : 0,
                    enableCreateSchedule: portalFeatures.enableCreateSchedule ? 1 : 0,
                    enableTimesheet: portalFeatures.enableTimesheet ? 1 : 0
                },
                enable_pay_period_targets: enablePayPeriodTargets ? 1 : 0,
                pay_period_type: enablePayPeriodTargets ? payPeriodType : null,
                pay_period_hours_target: enablePayPeriodTargets && payPeriodHoursTarget !== ''
                    ? parseFloat(payPeriodHoursTarget)
                    : null,
            };

            const response = await axiosClient.post('/store-settings', payload);
            setIsDirty(false);
            // Update initial settings after saving
            setInitialSettings({
                break_time_deduct_enabled: breakTimeDeductEnabled,
                add_salary_enabled: addSalaryEnabled,
                exclude_paid_leaves_from_absents: excludePaidLeavesFromAbsents,
                include_unpaid_leaves_in_absents: includeUnpaidLeavesInAbsents,
                default_daily_hours: defaultDailyHours,
                auto_add_paid_leave_to_salary: autoAddPaidLeaveToSalary,
                auto_deduct_unpaid_leave_from_salary: autoDeductUnpaidLeaveFromSalary,
                auto_deduct_absent_days_from_salary: autoDeductAbsentDaysFromSalary,
                dont_allow_break_after_end_time: dontAllowBreakAfterEndTime,
                allow_early_clock_in: allowEarlyClockIn,
                early_clock_in_minutes: earlyClockInMinutes,
                time_format: timeFormat,
                hours_format: hoursFormat,
                upload_photo_before_clock_in_out: uploadPhotoBeforeClockInOut,
                photo_mandatory_before_clock_in_out: photoMandatoryBeforeClockInOut,
                gps_location_enabled: gpsLocationEnabled,
                allow_manager_create_own_and_other_manager_shifts: allowManagerCreateOwnAndOtherManagerShifts,
                holiday_weekdays: { ...holidayWeekdays },
                portal_features: {
                    enableClockInTile: portalFeatures.enableClockInTile,
                    enableManagerView: portalFeatures.enableManagerView,
                    enableTimeOff: portalFeatures.enableTimeOff,
                    enableMyProfile: portalFeatures.enableMyProfile,
                    enableTasklist: portalFeatures.enableTasklist,
                    enableViewShifts: portalFeatures.enableViewShifts,
                    enableCreateSchedule: portalFeatures.enableCreateSchedule,
                    enableTimesheet: portalFeatures.enableTimesheet
                },
                show_payroll_stats_in_pos: showPayrollStatsInPos,
                enable_pay_period_targets: enablePayPeriodTargets,
                pay_period_type: payPeriodType,
                pay_period_hours_target: payPeriodHoursTarget,
            });
            shopify.toast.show(t('settings_saved'));
        } catch (error) {
            shopify.toast.show(t('error_saving_settings'), { error: true });
        }
    };

    const handleDiscard = () => {
        setBreakTimeDeductEnabled(initialSettings.break_time_deduct_enabled);
        setAddSalaryEnabled(initialSettings.add_salary_enabled);
        setExcludePaidLeavesFromAbsents(initialSettings.exclude_paid_leaves_from_absents);
        setIncludeUnpaidLeavesInAbsents(initialSettings.include_unpaid_leaves_in_absents);
        setDefaultDailyHours(initialSettings.default_daily_hours);
        setAutoAddPaidLeaveToSalary(initialSettings.auto_add_paid_leave_to_salary);
        setAutoDeductUnpaidLeaveFromSalary(initialSettings.auto_deduct_unpaid_leave_from_salary);
        setAutoDeductAbsentDaysFromSalary(initialSettings.auto_deduct_absent_days_from_salary);
        setDontAllowBreakAfterEndTime(initialSettings.dont_allow_break_after_end_time);
        setAllowEarlyClockIn(initialSettings.allow_early_clock_in);
        setEarlyClockInMinutes(initialSettings.early_clock_in_minutes);
        setTimeFormat(initialSettings.time_format || '24h');
        setHoursFormat(initialSettings.hours_format || 'standard');
        setUploadPhotoBeforeClockInOut(initialSettings.upload_photo_before_clock_in_out || false);
        setPhotoMandatoryBeforeClockInOut(initialSettings.photo_mandatory_before_clock_in_out !== undefined ? initialSettings.photo_mandatory_before_clock_in_out : true);
        setGpsLocationEnabled(initialSettings.gps_location_enabled || false);
        setAllowManagerCreateOwnAndOtherManagerShifts(initialSettings.allow_manager_create_own_and_other_manager_shifts || false);
        setHolidayWeekdays(initialSettings.holiday_weekdays || { // NEW: Reset the holiday weekdays setting
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: true,
            sunday: true
        });
        setPortalFeatures(initialSettings.portal_features || { // NEW: Reset the portal features settings
            enableClockInTile: true,
            enableManagerView: true,
            enableTimeOff: true,
            enableMyProfile: true,
            enableTasklist: true,
            enableViewShifts: true,
            enableCreateSchedule: false,
            enableTimesheet: true
        });
        setShowPayrollStatsInPos(initialSettings.show_payroll_stats_in_pos);
        setEnablePayPeriodTargets(initialSettings.enable_pay_period_targets || false);
        setPayPeriodType(initialSettings.pay_period_type || '');
        setPayPeriodHoursTarget(initialSettings.pay_period_hours_target || '');
        setIsDirty(false);
    };

    const handleEnablePayPeriodTargetsChange = (checked) => {
        setEnablePayPeriodTargets(checked);
        if (checked && !payPeriodType) {
            setPayPeriodType('monthly');
        }
        if (!checked) {
            setPayPeriodType('');
            setPayPeriodHoursTarget('');
        }
        setIsDirty(true);
    };

    const renderCheckbox = (label, featureState, setState, tooltipContent) => (
        <LegacyStack vertical>
            <LegacyStack.Item fill>
                <LegacyStack alignment="center">
                    <LegacyStack.Item fill>
                        <Checkbox
                            label={label}
                            checked={featureState}
                            onChange={(newValue) => handleCheckboxChange(setState, newValue)} // Pass newValue correctly
                        />
                    </LegacyStack.Item>
                    <LegacyStack.Item>
                        <Tooltip content={tooltipContent}>
                            <Icon source={QuestionCircleIcon} color="base" />
                        </Tooltip>
                    </LegacyStack.Item>
                </LegacyStack>
            </LegacyStack.Item>
        </LegacyStack>
    );

    // NEW: Render holiday weekday checkbox
    const renderHolidayWeekdayCheckbox = (day, label) => (
        <LegacyStack.Item>
            <Checkbox
                label={label}
                checked={holidayWeekdays[day]}
                onChange={(checked) => handleHolidayWeekdayChange(day, checked)}
            />
        </LegacyStack.Item>
    );

    // NEW: Render portal feature checkbox
    const renderPortalFeatureCheckbox = (feature, label, tooltipContent) => (
        <LegacyStack.Item>
            <LegacyStack alignment="center">
                <LegacyStack.Item fill>
                    <Checkbox
                        label={label}
                        checked={portalFeatures[feature]}
                        onChange={(checked) => handlePortalFeatureChange(feature, checked)}
                    />
                </LegacyStack.Item>
                <LegacyStack.Item>
                    <Tooltip content={tooltipContent}>
                        <Icon source={QuestionCircleIcon} color="base" />
                    </Tooltip>
                </LegacyStack.Item>
            </LegacyStack>
        </LegacyStack.Item>
    );

    return (
        <Page fullWidth>
            {isDirty && (
                <SaveBar open={isDirty} message={t('unsaved_changes')}>
                    <button type="button" onClick={handleDiscard}>
                        {t('discard')}
                    </button>
                    <button
                        type="button"
                        variant="primary"
                        onClick={async () => {
                            try {
                                await handleSave();
                            } catch (err) {
                                // Error handled silently
                            }
                        }}
                    >
                        {t('save')}
                    </button>
                </SaveBar>
            )}
            <TextContainer>
                {/* Page header - title and the product wordmark, as on Staff. */}
                <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <Text as="h1" variant="headingLg">{t('settings')}</Text>
                        <div style={{ marginTop: '2px' }}>
                            <Text as="p" variant="bodySm" tone="subdued">{t('settings_subtitle')}</Text>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col lg:flex-row gap-4">
                    <div className="lg:w-[40%]">
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <IconDisc icon={ClockIcon} tone="info" size={28} />
                            <div>
                                <Text as="h2" variant="headingMd">{t('clock_event_settings')}</Text>
                                <div style={{ marginTop: '2px' }}>
                                    <Text as="p" variant="bodySm" tone="subdued">{t('manage_clock_related_settings')}</Text>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="w-full lg:w-[60%]">
                        <Card sectioned>
                            <LegacyStack vertical spacing="extraTight">
                                {renderCheckbox(t('deduct_break_time_from_total_hours'), breakTimeDeductEnabled, setBreakTimeDeductEnabled, t('deduct_break_time_tooltip'))}
                                {renderCheckbox(t('calculate_weekly_monthly_salary_starts_after_first_clock_in_date'), addSalaryEnabled, setAddSalaryEnabled, t('calculate_salary_tooltip'))}
                                {renderCheckbox(t('dont_allow_staff_to_take_break_after_break_end_time'), dontAllowBreakAfterEndTime, setDontAllowBreakAfterEndTime, t('dont_allow_break_tooltip'))}
                                {/* NEW: Allow early clock-in setting with inline dropdown */}
                                <LegacyStack vertical>
                                    <LegacyStack.Item fill>
                                        <LegacyStack alignment="center">
                                            <LegacyStack.Item fill>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <Checkbox
                                                        label={t('allow_staff_to_clock_in')}
                                                        checked={allowEarlyClockIn}
                                                        onChange={(newValue) => handleCheckboxChange(setAllowEarlyClockIn, newValue)}
                                                    />
                                                    {allowEarlyClockIn && (
                                                        <>
                                                            <select
                                                                value={earlyClockInMinutes}
                                                                onChange={(e) => handleEarlyClockInMinutesChange(e.target.value)}
                                                                style={{
                                                                    padding: '4px 8px',
                                                                    border: '1px solid #c9cccf',
                                                                    borderRadius: '4px',
                                                                    fontSize: '14px',
                                                                    backgroundColor: 'white',
                                                                    minWidth: '120px',
                                                                    height: '32px'
                                                                }}
                                                            >
                                                                <option value="15">15 {t('minutes')}</option>
                                                                <option value="30">30 {t('minutes')}</option>
                                                                <option value="45">45 {t('minutes')}</option>
                                                                <option value="60">60 {t('minutes')}</option>
                                                            </select>
                                                            <span style={{ fontSize: '14px', color: '#6d7175' }}>{t('before_shift_start')}</span>
                                                        </>
                                                    )}
                                                    {!allowEarlyClockIn && (
                                                        <span style={{ fontSize: '14px', color: '#6d7175' }}>{t('before_shift_start')}</span>
                                                    )}
                                                </div>
                                            </LegacyStack.Item>
                                            <LegacyStack.Item>
                                                <Tooltip content={t('allow_early_clock_in_tooltip')}>
                                                    <Icon source={QuestionCircleIcon} color="base" />
                                                </Tooltip>
                                            </LegacyStack.Item>
                                        </LegacyStack>
                                    </LegacyStack.Item>
                                </LegacyStack>

                                {/* NEW: Show payroll stats in POS setting */}
                                {renderCheckbox(t('show_payroll_stats_of_staff_in_pos_to_managers'), showPayrollStatsInPos, setShowPayrollStatsInPos, t('show_payroll_stats_tooltip'))}

                                {/* NEW: Upload photo before clock in/out setting */}
                                {renderCheckbox(t('upload_photo_before_clock_in_out'), uploadPhotoBeforeClockInOut, setUploadPhotoBeforeClockInOut, t('upload_photo_before_clock_in_out_tooltip'))}

                                {/* NEW: Photo mandatory before clock in/out setting - only show if upload photo is enabled */}
                                {uploadPhotoBeforeClockInOut && renderCheckbox(t('photo_mandatory_before_clock_in_out'), photoMandatoryBeforeClockInOut, setPhotoMandatoryBeforeClockInOut, t('photo_mandatory_before_clock_in_out_tooltip'))}

                                {/* NEW: GPS location verification setting */}
                                {renderCheckbox(t('gps_location_enabled'), gpsLocationEnabled, setGpsLocationEnabled, t('gps_location_enabled_tooltip'))}

                                {/* NEW: Time format setting */}
                                <LegacyStack vertical>
                                    <LegacyStack.Item fill>
                                        <LegacyStack alignment="center">
                                            <LegacyStack.Item fill>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                    <span style={{ fontSize: '14px', color: '#202223' }}>{t('time_format')}:</span>
                                                    <div style={{ minWidth: '200px' }}>
                                                        <Select
                                                            label={t('time_format')}
                                                            labelHidden
                                                            options={[
                                                                { label: t('24_hour'), value: '24h' },
                                                                { label: t('12_hour'), value: '12h' },
                                                            ]}
                                                            value={timeFormat}
                                                            onChange={(value) => {
                                                                setTimeFormat(value);
                                                                setIsDirty(true);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </LegacyStack.Item>
                                            <LegacyStack.Item>
                                                <Tooltip content={t('time_format_tooltip')}>
                                                    <Icon source={QuestionCircleIcon} color="base" />
                                                </Tooltip>
                                            </LegacyStack.Item>
                                        </LegacyStack>
                                    </LegacyStack.Item>
                                </LegacyStack>

                                {/* NEW: Hours format setting */}
                                <LegacyStack vertical>
                                    <LegacyStack.Item fill>
                                        <LegacyStack alignment="center">
                                            <LegacyStack.Item fill>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                    <span style={{ fontSize: '14px', color: '#202223' }}>{t('hours_format')}:</span>
                                                    <div style={{ minWidth: '200px' }}>
                                                        <Select
                                                            label={t('hours_format')}
                                                            labelHidden
                                                            options={[
                                                                { label: t('standard_format'), value: 'standard' },
                                                                { label: t('decimal_format'), value: 'decimal' },
                                                            ]}
                                                            value={hoursFormat}
                                                            onChange={(value) => {
                                                                setHoursFormat(value);
                                                                setIsDirty(true);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </LegacyStack.Item>
                                            <LegacyStack.Item>
                                                <Tooltip content={t('hours_format_tooltip')}>
                                                    <Icon source={QuestionCircleIcon} color="base" />
                                                </Tooltip>
                                            </LegacyStack.Item>
                                        </LegacyStack>
                                    </LegacyStack.Item>
                                </LegacyStack>
                            </LegacyStack>
                        </Card>
                    </div>
                </div>

                {/* Pay period hour targets */}
                <div className="flex flex-col lg:flex-row gap-4 mt-6">
                    <div className="lg:w-[40%]">
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <IconDisc icon={TargetIcon} tone="critical" size={28} />
                            <div>
                                <Text as="h2" variant="headingMd">{t('pay_period_targets_settings')}</Text>
                                <div style={{ marginTop: '2px' }}>
                                    <Text as="p" variant="bodySm" tone="subdued">{t('pay_period_targets_settings_description')}</Text>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="w-full lg:w-[60%]">
                        <Card sectioned>
                            <LegacyStack vertical spacing="extraTight">
                                <LegacyStack vertical>
                                    <LegacyStack.Item fill>
                                        <LegacyStack alignment="center">
                                            <LegacyStack.Item fill>
                                                <Checkbox
                                                    label={t('enable_pay_period_targets')}
                                                    checked={enablePayPeriodTargets}
                                                    onChange={handleEnablePayPeriodTargetsChange}
                                                />
                                            </LegacyStack.Item>
                                            <LegacyStack.Item>
                                                <Tooltip content={t('enable_pay_period_targets_tooltip')}>
                                                    <Icon source={QuestionCircleIcon} color="base" />
                                                </Tooltip>
                                            </LegacyStack.Item>
                                        </LegacyStack>
                                    </LegacyStack.Item>
                                </LegacyStack>

                                {enablePayPeriodTargets && (
                                    <>
                                        <LegacyStack vertical>
                                            <LegacyStack.Item fill>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                    <span style={{ fontSize: '14px', color: '#202223' }}>{t('pay_period_type')}:</span>
                                                    <div style={{ minWidth: '200px' }}>
                                                        <Select
                                                            label={t('pay_period_type')}
                                                            labelHidden
                                                            options={[
                                                                { label: t('select_pay_period'), value: '' },
                                                                { label: t('pay_period_daily'), value: 'daily' },
                                                                { label: t('pay_period_weekly'), value: 'weekly' },
                                                                { label: t('pay_period_monthly'), value: 'monthly' },
                                                            ]}
                                                            value={payPeriodType}
                                                            onChange={(value) => {
                                                                setPayPeriodType(value);
                                                                setIsDirty(true);
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </LegacyStack.Item>
                                        </LegacyStack>

                                        {payPeriodType === 'daily' && (
                                            <TextField
                                                label={t('pay_period_daily_hours_target')}
                                                type="number"
                                                value={payPeriodHoursTarget}
                                                onChange={(value) => {
                                                    setPayPeriodHoursTarget(value);
                                                    setIsDirty(true);
                                                }}
                                                min={0.25}
                                                max={24}
                                                step={0.25}
                                                autoComplete="off"
                                                helpText={t('pay_period_daily_hours_target_help')}
                                            />
                                        )}

                                        {payPeriodType === 'weekly' && (
                                            <TextField
                                                label={t('pay_period_weekly_hours_target')}
                                                type="number"
                                                value={payPeriodHoursTarget}
                                                onChange={(value) => {
                                                    setPayPeriodHoursTarget(value);
                                                    setIsDirty(true);
                                                }}
                                                min={1}
                                                max={168}
                                                step={0.5}
                                                autoComplete="off"
                                                helpText={t('pay_period_weekly_hours_target_help')}
                                            />
                                        )}

                                        {payPeriodType === 'monthly' && (
                                            <TextField
                                                label={t('pay_period_monthly_hours_target')}
                                                type="number"
                                                value={payPeriodHoursTarget}
                                                onChange={(value) => {
                                                    setPayPeriodHoursTarget(value);
                                                    setIsDirty(true);
                                                }}
                                                min={1}
                                                max={744}
                                                step={1}
                                                autoComplete="off"
                                                helpText={t('pay_period_monthly_hours_target_help')}
                                            />
                                        )}
                                    </>
                                )}
                            </LegacyStack>
                        </Card>
                    </div>
                </div>

                {/* Absence Calculation Settings */}
                <div className="flex flex-col lg:flex-row gap-4 mt-6">
                    <div className="lg:w-[40%]">
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <IconDisc icon={CalendarIcon} tone="warning" size={28} />
                            <div>
                                <Text as="h2" variant="headingMd">{t('absence_calculation_settings')}</Text>
                                <div style={{ marginTop: '2px' }}>
                                    <Text as="p" variant="bodySm" tone="subdued">{t('configure_how_absences_are_calculated')}</Text>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="w-full lg:w-[60%]">
                        <Card sectioned>
                            <LegacyStack vertical spacing="extraTight">
                                {renderCheckbox(t('exclude_paid_leaves_from_total_absences'), excludePaidLeavesFromAbsents, setExcludePaidLeavesFromAbsents, t('exclude_paid_leaves_tooltip'))}
                                {renderCheckbox(t('include_unpaid_leaves_in_total_absences'), includeUnpaidLeavesInAbsents, setIncludeUnpaidLeavesInAbsents, t('include_unpaid_leaves_tooltip'))}
                                {/* NEW: Auto add paid leave setting */}
                                {renderCheckbox(t('automatically_add_paid_leaves_to_salary'), autoAddPaidLeaveToSalary, setAutoAddPaidLeaveToSalary, t('auto_add_paid_leaves_tooltip'))}

                                {/* NEW: Auto deduct unpaid leave setting */}
                                {renderCheckbox(t('automatically_deduct_unpaid_leaves_from_salary'), autoDeductUnpaidLeaveFromSalary, setAutoDeductUnpaidLeaveFromSalary, t('auto_deduct_unpaid_leaves_tooltip'))}

                                {/* NEW: Auto deduct absent days setting */}
                                {renderCheckbox(t('automatically_deduct_absences_from_salary'), autoDeductAbsentDaysFromSalary, setAutoDeductAbsentDaysFromSalary, t('auto_deduct_absences_tooltip'))}

                                <LegacyStack vertical>
                                    <LegacyStack.Item fill>
                                        <LegacyStack alignment="center">
                                            <LegacyStack.Item fill>
                                                <div style={{ maxWidth: '200px' }}>
                                                    <TextField
                                                        label={t('default_daily_working_hours')}
                                                        type="number"
                                                        value={defaultDailyHours}
                                                        onChange={handleTextFieldChange}
                                                        min={1}
                                                        max={24}
                                                        suffix={t('hours_suffix')}
                                                        autoComplete="off"
                                                    />
                                                </div>
                                            </LegacyStack.Item>
                                            <LegacyStack.Item>
                                                <Tooltip content={t('default_daily_hours_tooltip')}>
                                                    <Icon source={QuestionCircleIcon} color="base" />
                                                </Tooltip>
                                            </LegacyStack.Item>
                                        </LegacyStack>
                                    </LegacyStack.Item>
                                </LegacyStack>
                            </LegacyStack>
                        </Card>
                    </div>
                </div>

                {/* NEW: Holiday Rules Settings */}
                <div className="flex flex-col lg:flex-row gap-4 mt-6">
                    <div className="lg:w-[40%]">
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <IconDisc icon={CalendarCheckIcon} tone="magic" size={28} />
                            <div>
                                <Text as="h2" variant="headingMd">{t('holiday_rules_settings')}</Text>
                                <div style={{ marginTop: '2px' }}>
                                    <Text as="p" variant="bodySm" tone="subdued">{t('configure_which_days_of_the_week_are_considered_holidays')}</Text>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="w-full lg:w-[60%]">
                        <Card sectioned>
                            <LegacyStack vertical spacing="loose">
                                <p>{t('select_the_days_you_want_to_mark_as_holidays')}</p>
                                <LegacyStack wrap>
                                    {renderHolidayWeekdayCheckbox('monday', t('monday'))}
                                    {renderHolidayWeekdayCheckbox('tuesday', t('tuesday'))}
                                    {renderHolidayWeekdayCheckbox('wednesday', t('wednesday'))}
                                    {renderHolidayWeekdayCheckbox('thursday', t('thursday'))}
                                    {renderHolidayWeekdayCheckbox('friday', t('friday'))}
                                    {renderHolidayWeekdayCheckbox('saturday', t('saturday'))}
                                    {renderHolidayWeekdayCheckbox('sunday', t('sunday'))}
                                </LegacyStack>
                                <LegacyStack.Item>

                                    <div style={{ display: 'flex', margin: '0px' }}>
                                        <Tooltip content={t('holiday_days_tooltip')}>
                                            <div style={{ margin: '0px' }}>
                                                <Icon source={QuestionCircleIcon} color="base" />
                                            </div>
                                        </Tooltip>
                                        <span style={{ marginLeft: '8px' }}>{t('holidays_will_not_be_counted_as_absences')}</span>
                                    </div>

                                </LegacyStack.Item>
                            </LegacyStack>
                        </Card>

                    </div>
                </div>

                {/* Staff Portal Settings - Combined Features and Link */}
                <div className="flex flex-col lg:flex-row gap-4 mt-6">
                    <div className="lg:w-[40%]">
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                            <IconDisc icon={MobileIcon} tone="success" size={28} />
                            <div>
                                <Text as="h2" variant="headingMd">{t('staff_portal')}</Text>
                                <div style={{ marginTop: '2px' }}>
                                    <Text as="p" variant="bodySm" tone="subdued">{t('control_portal_features_and_access_for_your_staff')}</Text>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="w-full lg:w-[60%]">
                        <Card sectioned>
                            <LegacyStack vertical spacing="loose">
                                <p>{t('your_staff_can_access_the_portal_using_this_link')}</p>
                                <div className='flex justify-between mb-[16px] w-full'>
                                    <div className='w-[90%]'>
                                        <TextField
                                            value={`https://portal.zonstaff.com/?shopDomain=${shopDomain || ''}`}
                                            readOnly
                                            autoComplete="off"
                                            style={{ width: '100%', fontSize: '16px' }} // Increased font size
                                        />
                                    </div>
                                    <div className='w-[10%]'
                                        style={{
                                            marginLeft: '2px', // Reduced space between field and icon
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                        onClick={() => {
                                            navigator.clipboard.writeText(`https://portal.zonstaff.com/?shopDomain=${shopDomain || ''}`);
                                            shopify.toast.show(t('link_copied'));
                                        }}
                                    >
                                        <Tooltip content={t('copy_to_clipboard')}>
                                            <Icon source={ClipboardCheckIcon} color="base" />
                                        </Tooltip>
                                    </div>
                                </div>

                                <div style={{ borderTop: '1px solid #E1E3E5', paddingTop: '16px' }}>
                                    <InlineStack wrap={true} gap="400" blockAlign="start" style={{ marginTop: '12px' }}>
                                        <Checkbox
                                            label={t('clock_in')}
                                            checked={portalFeatures.enableClockInTile}
                                            onChange={(checked) => handlePortalFeatureChange('enableClockInTile', checked)}
                                            helpText={t('staff_can_clock_in_out')}
                                        />
                                        <Checkbox
                                            label={t('manager_view')}
                                            checked={portalFeatures.enableManagerView}
                                            onChange={(checked) => handlePortalFeatureChange('enableManagerView', checked)}
                                            helpText={t('access_management_features')}
                                        />
                                        <Checkbox
                                            label={t('time_off')}
                                            checked={portalFeatures.enableTimeOff}
                                            onChange={(checked) => handlePortalFeatureChange('enableTimeOff', checked)}
                                            helpText={t('submit_time_off_requests')}
                                        />
                                        <Checkbox
                                            label={t('my_profile_shifts')}
                                            checked={portalFeatures.enableMyProfile}
                                            onChange={(checked) => handlePortalFeatureChange('enableMyProfile', checked)}
                                            helpText={t('view_edit_profile_info')}
                                        />
                                        <Checkbox
                                            label={t('tasklist')}
                                            checked={portalFeatures.enableTasklist}
                                            onChange={(checked) => handlePortalFeatureChange('enableTasklist', checked)}
                                            helpText={t('view_tasklist')}
                                        />
                                        <Checkbox
                                            label={t('view_shifts')}
                                            checked={portalFeatures.enableViewShifts}
                                            onChange={(checked) => handlePortalFeatureChange('enableViewShifts', checked)}
                                            helpText={t('view_assigned_shifts')}
                                        />
                                        <Checkbox
                                            label={t('create_schedule') || 'Create Schedule'}
                                            checked={portalFeatures.enableCreateSchedule}
                                            onChange={(checked) => handlePortalFeatureChange('enableCreateSchedule', checked)}
                                            helpText={t('manager_schedule_creation') || 'Allow managers to create shifts via POS portal'}
                                        />
                                        <Checkbox
                                            label={t('timesheet')}
                                            checked={portalFeatures.enableTimesheet}
                                            onChange={(checked) => handlePortalFeatureChange('enableTimesheet', checked)}
                                            helpText={t('staff_can_view_monthly_timesheet')}
                                        />
                                    </InlineStack>
                                    {portalFeatures.enableCreateSchedule && (
                                        <div style={{ marginTop: '16px', borderTop: '1px dashed #E1E3E5', paddingTop: '16px', paddingLeft: '8px' }}>
                                            <Checkbox
                                                label={t('allow_manager_create_own_and_other_manager_shifts') || 'Allow managers to create their own and other manager shifts'}
                                                checked={allowManagerCreateOwnAndOtherManagerShifts}
                                                onChange={(checked) => handleCheckboxChange(setAllowManagerCreateOwnAndOtherManagerShifts, checked)}
                                                helpText={t('allow_manager_create_own_and_other_manager_shifts_help') || 'If enabled, managers can schedule themselves and other managers.'}
                                            />
                                        </div>
                                    )}
                                </div>
                            </LegacyStack>
                        </Card>
                    </div>
                </div>
            </TextContainer>
        </Page>
    );
}
